import { BUILT_IN_VARIABLES, templateVariables } from './testSend'

/**
 * Rules for the Create and Edit Template form that do not need React:
 * required fields per channel, the placeholder syntax each channel is sent
 * with, the variables a template declares, and the request bodies.
 */

export const EMAIL = 'email'
export const WHATSAPP = 'whatsapp'
export const SMS = 'sms'
export const VOICE = 'voice'
export const PUSH = 'push_notification'
export const SLACK = 'slack'

/**
 * How a variable is written in each channel's template.
 *
 * lambda-comms-internal-trigger fills email, SMS, voice, push and Slack
 * templates with Python's string.Template, which substitutes `${name}` and
 * leaves `{{name}}` in the message as literal text. WhatsApp templates are
 * filled by Meta, which uses `{{name}}`.
 */
export const variableExample = (commType, name = 'name') =>
    commType === WHATSAPP ? `{{${name}}}` : `\${${name}}`

// A variable written either way: ${name} or {{name}}.
export const VARIABLE_IN_TEXT =
    /\$\{\s*([A-Za-z_]\w*)\s*\}|\{\{\s*([A-Za-z_]\w*)\s*\}\}/g

/** The text with every variable written in the channel's own style. */
export const inChannelStyle = (text, commType) =>
    String(text ?? '').replace(VARIABLE_IN_TEXT, (_, a, b) =>
        variableExample(commType, a || b)
    )

/**
 * Wrap a voice script in <speak> tags unless it already is one, as the
 * production comms app does; the voice provider expects SSML.
 */
export const ensureSpeakTags = (script) => {
    let out = String(script ?? '').trim()
    if (!out) return out
    if (!out.startsWith('<speak>')) out = `<speak>${out}`
    if (!out.endsWith('</speak>')) out = `${out}</speak>`
    return out
}

// India's DLT template ids are 19 digits; accept any run of digits so a
// differently sized id is not rejected outright.
const DLT_ID_RE = /^\d{10,25}$/

// A variable name the senders can substitute: string.Template's identifier,
// and the {{name}} form the WhatsApp registration converts.
export const VARIABLE_NAME_RE = /^[A-Za-z_][A-Za-z0-9_]*$/

/**
 * A template name as the user types it: lowercase, with each space and
 * hyphen turned into an underscore (the old app's rule, which removed them).
 *
 * WhatsApp names are registered as typed (the AWS registration sends the
 * name straight to WhatsApp, which accepts only lowercase letters, digits
 * and underscores), so for WhatsApp anything else is dropped too: "Class
 * Reminder!" was refused at registration and now becomes "class_reminder".
 *
 * @param {string} commType the template's channel
 * @param {string} value what was typed
 * @returns {string}
 */
export const normaliseTemplateName = (commType, value) => {
    const name = String(value ?? '')
        .toLowerCase()
        .replace(/[\s-]/g, '_')
    return commType === WHATSAPP ? name.replace(/[^a-z0-9_]/g, '') : name
}

/** Unique {{variable}} names in a WhatsApp body, in order of appearance. */
export const whatsappVariables = (body) => [
    ...new Set(
        [...String(body ?? '').matchAll(/{{\s*([a-zA-Z0-9_]+)\s*}}/g)].map(
            (m) => m[1]
        )
    ),
]

/**
 * The variables a template declares (its `others` list), in order.
 *
 * `others` is what a send has to supply: the WhatsApp sender builds the
 * message's parameters from it, in its order (lambda-comms-internal-trigger
 * `util/whatsapp.py`), and a pipeline lists its templates' `others` as the
 * event's required fields. The Create form never saved it, so templates made
 * in Comms V2 declared nothing and a WhatsApp send went out with blank values.
 *
 * - WhatsApp: the body's {{name}} placeholders in order of appearance, the
 *   same order as `others_example`, which the registration numbers {{1}},
 *   {{2}}... by. Nothing else: the parameter count must match the template.
 * - Every other channel: the ${name} placeholders in `texts` (subject, body,
 *   calendar title), then `extra`, the names the user added (the old app's
 *   "Template Variables"), without duplicates, invalid names, or the fields
 *   the sender fills by itself for the channel (receiver details, session
 *   times).
 *
 * @param {{commType: string, texts?: string[], extra?: string[]}} args
 * @returns {string[]}
 */
export const declaredVariables = ({ commType, texts = [], extra = [] }) => {
    if (commType === WHATSAPP) return whatsappVariables(texts.join('\n'))
    const builtIn = BUILT_IN_VARIABLES[commType] ?? []
    return [
        ...templateVariables(...texts),
        ...(extra ?? []).map((v) => String(v ?? '').trim()),
    ]
        .filter((v) => VARIABLE_NAME_RE.test(v) && !builtIn.includes(v))
        .filter((v, i, all) => all.indexOf(v) === i)
}

/**
 * Field errors for the non-WhatsApp channels, keyed by field. An empty object
 * means the form can be submitted. Each message says what to do, because a
 * red outline alone did not.
 */
export const validateTemplateForm = ({
    commType,
    templateName,
    subject,
    approverType,
    text,
    dltTemplateId,
}) => {
    const errors = {}
    if (!String(templateName ?? '').trim())
        errors.templateName = 'Enter a template name'
    if (commType === EMAIL && !String(subject ?? '').trim())
        errors.subject = 'Enter the email subject'
    if (commType === PUSH && !String(subject ?? '').trim())
        errors.subject = 'Enter the notification title'
    if (commType !== SMS && !approverType)
        errors.approverType = 'Choose who approves this template'
    if (
        [SMS, VOICE, PUSH, SLACK].includes(commType) &&
        !String(text ?? '').trim()
    )
        errors.text = 'Write the message content'
    if (commType === SMS && !DLT_ID_RE.test(String(dltTemplateId ?? '').trim()))
        errors.dltTemplateId = 'Enter the DLT template ID (digits only)'
    return errors
}

/**
 * The /comms/v2/template body for SMS, voice, push and Slack.
 *
 * SMS sends its text and DLT id (the production comms app's contract; the
 * Pinpoint template name it used to send is not what the API reads). Push
 * sends its title as `subject`, which the sender uses as the notification
 * title. `others` is the template's declared variables (declaredVariables).
 */
export const buildTextTemplatePayload = ({
    commType,
    templateName,
    comment,
    subject,
    approverType,
    text,
    dltTemplateId,
    others = [],
}) => {
    const body = {
        template_name: String(templateName).trim(),
        comm_type: commType,
        comment: comment ?? '',
        subject: commType === PUSH ? String(subject ?? '').trim() : '',
        template_data:
            commType === VOICE ? ensureSpeakTags(text) : String(text).trim(),
        others,
    }
    if (commType === SMS) body.dlt_template_id = String(dltTemplateId).trim()
    else body.approver_type = approverType
    return body
}

// Fields GET /comms/v2/template/{id} adds to the row for reading (the S3
// files' contents, a signed media URL); they are not template fields.
const READ_ONLY_FIELDS = [
    'extracted_body',
    'extracted_design_json',
    'pre_signed_url',
]

/**
 * The PUT /comms/v2/template/{id} body: the saved row with `changes` on top.
 *
 * The update replaces the whole row with what it is sent, and the fields it
 * is not sent take their defaults: `status` becomes "inactive", `others` and
 * `attachments` empty, the sender fields blank. So an edit must send every
 * field it did not change as saved, as the old app does (it sends the cloned
 * row). The fields the API only adds for reading are left out.
 *
 * @param {object} row the template as GET /comms/v2/template/{id} returned it
 * @param {object} changes the fields the form sets
 * @returns {object}
 */
export const editPayload = (row, changes) => {
    const saved = { ...(row ?? {}) }
    READ_ONLY_FIELDS.forEach((field) => delete saved[field])
    return { ...saved, ...changes }
}
